// apps/admin-ui/src/components/ui/IconRail.tsx
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Megaphone,
  CalendarDays,
  Award,
  FileText,
  Activity,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { cn } from './cn.ts';
import { MapLogo } from './MapLogo.tsx';

interface RailArea {
  id: string;
  icon: LucideIcon;
  label: string;
  path: string;
  matchPrefixes: string[];
}

const RAIL_AREAS: RailArea[] = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/',            matchPrefixes: [] },
  { id: 'audience',  icon: Users,           label: 'Audience',  path: '/customers',   matchPrefixes: ['/customers', '/segments'] },
  { id: 'marketing', icon: Megaphone,       label: 'Marketing', path: '/automations', matchPrefixes: ['/automations', '/campaigns', '/templates'] },
  { id: 'events',    icon: CalendarDays,    label: 'Events',    path: '/events',      matchPrefixes: ['/events'] },
  { id: 'loyalty',   icon: Award,           label: 'Loyalty',   path: '/loyalty',     matchPrefixes: ['/loyalty'] },
  { id: 'content',   icon: FileText,        label: 'Content',   path: '/spaces',      matchPrefixes: ['/spaces', '/media', '/shortlinks', '/questionnaires'] },
  { id: 'data',      icon: Activity,        label: 'Data',      path: '/signals',     matchPrefixes: ['/signals', '/signal-types', '/analytics'] },
];

const SETTINGS_PREFIXES = ['/integrations', '/cms/tokens', '/cms/block-defs', '/team', '/settings'];

const SETTINGS_AREA = {
  id: 'settings', icon: Settings, label: 'Settings', path: '/integrations',
} as const;

// Returns which rail area is active based on the current pathname.
// Dashboard is active only on exact '/'.
export function getActiveArea(pathname: string): string {
  if (pathname === '/') return 'dashboard';
  for (const area of RAIL_AREAS) {
    if (area.matchPrefixes.some((p) => pathname.startsWith(p))) return area.id;
  }
  if (SETTINGS_PREFIXES.some((p) => pathname.startsWith(p))) return 'settings';
  return '';
}

interface IconRailProps {
  className?: string;
}

export function IconRail({ className }: IconRailProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeArea = getActiveArea(location.pathname);

  function goTo(area: { path: string }) {
    navigate(area.path);
  }

  function railIconClass(isActive: boolean) {
    return cn(
      'w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
      isActive
        ? 'bg-violet-600 text-white'
        : 'text-zinc-400 group-hover:bg-zinc-800 group-hover:text-zinc-100',
    );
  }

  function railLabelClass(isActive: boolean) {
    return cn(
      'text-[10px] leading-tight font-medium transition-colors',
      isActive ? 'text-white' : 'text-zinc-500 group-hover:text-zinc-300',
    );
  }

  return (
    <div className={cn('flex flex-col items-center bg-zinc-900 w-[60px] shrink-0 py-3 gap-0.5', className)}>
      <div className="mb-3">
        <MapLogo variant="mark" theme="light" className="w-7 h-7" />
      </div>

      {RAIL_AREAS.map((area) => {
        const Icon = area.icon;
        const isActive = activeArea === area.id;
        return (
          <button
            key={area.id}
            aria-label={area.label}
            onClick={() => goTo(area)}
            className="group flex flex-col items-center gap-0.5 py-1 w-full"
          >
            <div className={railIconClass(isActive)}>
              <Icon size={17} />
            </div>
            <span className={railLabelClass(isActive)}>{area.label}</span>
          </button>
        );
      })}

      <div className="flex-1" />

      {/* Settings — pinned to bottom */}
      <button
        aria-label={SETTINGS_AREA.label}
        onClick={() => goTo(SETTINGS_AREA)}
        className="group flex flex-col items-center gap-0.5 py-1 w-full"
      >
        <div className={railIconClass(activeArea === 'settings')}>
          <SETTINGS_AREA.icon size={17} />
        </div>
        <span className={railLabelClass(activeArea === 'settings')}>{SETTINGS_AREA.label}</span>
      </button>
    </div>
  );
}
